import { cn } from "@/lib/cn"
import { fmtRelativeTime } from "@/lib/format"
import type { VersionDotRow } from "@/types/version_dot"

type Props = {
  versions: VersionDotRow[]
  // Row id of the selected version (matches `VersionDotRow.id`).
  // Used by both Skills (SkillVersion id) and Escalation Policies
  // (EscalationPolicySet id) — same dot, different parent table.
  selectedVersionId: number
  // Emits the clicked row id; parent maps id → URL nav.
  onSelect: (versionId: number) => void
}

// Horizontal timeline strip: one dot per version in the lineage,
// colored by status. Click a dot to switch the page's selected version.
export function VersionTimeline({ versions, selectedVersionId, onSelect }: Props) {
  // Show oldest → newest left-to-right (reverse of the table's newest-first).
  const ordered = [...versions].sort((a, b) => a.version - b.version)

  return (
    <div className="flex items-center gap-1 overflow-x-auto py-1">
      {ordered.map((v, idx) => {
        const here = v.id === selectedVersionId
        return (
          <div key={v.id} className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onSelect(v.id)}
              className={cn(
                "group flex flex-col items-center gap-1 rounded-control px-3 py-2 text-xs transition",
                here ? "bg-white/5" : "hover:bg-white/[0.03]",
              )}
            >
              <Dot status={v.status} highlighted={here} />
              <span className={cn("font-medium", here ? "text-app-text" : "text-app-muted")}>
                v{v.version}
              </span>
              <span className="text-[10px] uppercase tracking-wide text-app-subtle">
                {v.status}
              </span>
              <span className="text-[10px] text-app-subtle">
                {(v.lastEditedByUserName || v.createdByUserName) && (
                  <>{v.lastEditedByUserName ?? v.createdByUserName} · </>
                )}
                {fmtRelativeTime(v.promotedAt ?? v.updatedAt)}
              </span>
            </button>
            {idx < ordered.length - 1 && (
              <span className="h-px w-6 bg-border" aria-hidden />
            )}
          </div>
        )
      })}
    </div>
  )
}

function Dot({ status, highlighted }: { status: VersionDotRow["status"]; highlighted: boolean }) {
  const color =
    status === "live"     ? "bg-vip-gold border-vip-gold"
  : status === "draft"    ? "bg-brand-red border-brand-red"
  :                          "border-border-strong bg-transparent"
  return (
    <span
      className={cn(
        "h-3 w-3 rounded-full border transition",
        color,
        highlighted ? "ring-2 ring-offset-2 ring-offset-app-bg" : "",
        highlighted && status === "live"   ? "ring-vip-gold/50" : "",
        highlighted && status === "draft"  ? "ring-brand-red/50" : "",
        highlighted && status === "archived" ? "ring-border-strong" : "",
      )}
      aria-hidden
    />
  )
}
